import * as CL from '@design-system/component-library';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { RelatedPartyRoles } from '../../../../common/enums.js';
import { fieldCantBeEmptyMsg, selectMsg, t } from '../../../../common/i18n/index.js';
import { ContactDropdown } from '../../../../common/react-hook-form/fields/ContactDropdown.js';
import { scrollToError } from '../../utils/contactOrPurposeUtils.js';
import type { ContactsUpdateMethods } from '../../placeOrderTypes.js';
import type { PlaceOrderFormDataCoCo, PlaceOrderItemUnitCoCo } from '../../placeOrderTypesCoCo.js';
import type { InstallationContactHandle } from '../productConfigTypesCoCo.js';
import type { Path } from 'react-hook-form';

import './InstallationContactSectionCoCo.scss';

interface InstallationContactSectionProps {
  orderItem: PlaceOrderItemUnitCoCo;
  contactsUpdateMethods: ContactsUpdateMethods;
  hasUserAsInitialContact?: boolean;
}

const getFieldName = (fieldPrefix: string, name: keyof PlaceOrderItemUnitCoCo): Path<PlaceOrderFormDataCoCo> =>
  `${fieldPrefix}.${name}` as Path<PlaceOrderFormDataCoCo>;

// The accordion keeps its open state in the DOM, so a collapsed one hides the error unless it is reopened first.
const reopenParentAccordion = (element: HTMLElement | null) => {
  const content = element?.closest('[role="region"][aria-hidden="true"]');
  const heading = content?.getAttribute('aria-labelledby');
  if (heading) {
    document.getElementById(heading)?.click();
  }
};

export const InstallationContactSectionCoCo = forwardRef<InstallationContactHandle, InstallationContactSectionProps>(
  ({ orderItem, contactsUpdateMethods, hasUserAsInitialContact = true }, ref) => {
    const { clearErrors, setError, setValue } = useFormContext<PlaceOrderFormDataCoCo>();
    const orderItemUnits = (useWatch({ name: 'products.placeOrderItemUnits' }) as PlaceOrderItemUnitCoCo[]) ?? [];
    const orderItemIndex = orderItemUnits.findIndex(
      item => item.basketItemId === orderItem.basketItemId && item.basketItemIndex === orderItem.basketItemIndex
    );
    const fieldPrefix = `products.placeOrderItemUnits.${orderItemIndex}` as const;
    const userContact = useWatch({ name: getFieldName(fieldPrefix, 'userContact') });
    const installationContact = useWatch({ name: getFieldName(fieldPrefix, RelatedPartyRoles.INSTALLATION_CONTACT) });
    const [useUserContactForInstallation, setUseUserContactForInstallation] = useState(hasUserAsInitialContact);
    const installationContacts = contactsUpdateMethods.contacts?.filter(contact => contact.contactId !== userContact);
    const installationContactFieldName = getFieldName(fieldPrefix, RelatedPartyRoles.INSTALLATION_CONTACT);
    const installationContactSectionId = `installation-contact-${orderItem.basketItemId}-${orderItem.basketItemIndex}`;

    useEffect(() => {
      if (useUserContactForInstallation && userContact && installationContact !== userContact) {
        setValue(installationContactFieldName, userContact, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }, [installationContact, installationContactFieldName, setValue, userContact, useUserContactForInstallation]);

    useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          if (installationContact) {
            clearErrors(installationContactFieldName);
            return true;
          }
          setError(installationContactFieldName, {
            type: 'required',
            message: t.VPVR(fieldCantBeEmptyMsg),
          });
          return false;
        },
        scrollToFirstError: () => {
          reopenParentAccordion(document.getElementById(installationContactSectionId));
          scrollToError(installationContactSectionId);
        },
        getErrorCount: () => (installationContact ? 0 : 1),
      }),
      [clearErrors, installationContact, installationContactSectionId, installationContactFieldName, setError]
    );

    if (orderItemIndex < 0) {
      return null;
    }

    const handleInstallationContactChange = (isChecked: boolean) => {
      setUseUserContactForInstallation(isChecked);
      setValue(installationContactFieldName, isChecked ? userContact : undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      if (isChecked) {
        clearErrors(installationContactFieldName);
      }
    };

    return (
      <div className="of-installation-contact-coco" id={installationContactSectionId}>
        <div className="of-installation-contact-coco__checkbox-row">
          <CL.Checkbox
            checked={useUserContactForInstallation}
            onChange={event => handleInstallationContactChange(event.currentTarget.checked)}
            label={t.ME73('This person will be available at the installation location')}
          />
          <span className="of-installation-contact-coco__popover-trigger">
            <CL.Popover
              triggerElement={<CL.Icon icon="information" size="s" />}
              placement="top"
              i18n_popover_contentText={t.CCTC(
                'Engineer can call during the installation if needed (e.g. for access, instructions, or additional information).'
              )}
            />
          </span>
        </div>
        {!useUserContactForInstallation && (
          <div className={dsClass.MARGIN_TOP_3}>
            <div className={`${dsClass.FONT_WEIGHT_600} ${dsClass.MARGIN_BOTTOM_2}`}>
              {t.N6FO('Installation contact ')}
              <span className="of-installation-contact-coco__popover-trigger">
                <CL.Popover
                  triggerElement={<CL.Icon icon="information" size="s" />}
                  placement="top"
                  i18n_popover_contentText={t.CCTC(
                    'Select a person who can be available at the installation location who the engineer can call during the installation if needed (e.g. for access, instructions, or additional information).'
                  )}
                />
              </span>
            </div>
            <ContactDropdown
              contacts={installationContacts}
              name={installationContactFieldName}
              className="of-installation-contact-coco__dropdown"
              labelText=""
              labelClass={dsClass.SR_ONLY}
              canAddNewContacts={true}
              placeHolderText={t.QRYV(selectMsg)}
              required={true}
              onChangeContact={contactId => {
                setValue(getFieldName(fieldPrefix, RelatedPartyRoles.INSTALLATION_CONTACT), contactId, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setUseUserContactForInstallation(false);
                clearErrors(installationContactFieldName);
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

InstallationContactSectionCoCo.displayName = 'InstallationContactSectionCoCo';
